import { useCallback } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CommentTextArea } from "@/modules/patients/detail/health-information/comment-text-area"
import { ConfirmedBy } from "@/modules/patients/detail/health-information/confirmed-by"
import { ReadOnlyFormField } from "@/modules/patients/detail/health-information/readonly-form-field"
import { SelectFormField } from "@/modules/patients/detail/health-information/select-form-field"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  DrinkingHabit,
  SmokingHabit,
  SocialHistoryFragment$key,
} from "@/modules/patients/services/__generated__/SocialHistoryFragment.graphql"
import { Ch_updatePatientSocialHistoryInput } from "@/modules/patients/services/ch/__generated__/chUpdatePatientSocialHistoryMutation.graphql"
import { Ch_verifyHealthInformationInput } from "@/modules/patients/services/ch/__generated__/chVerifyHealthInformationMutation.graphql"
import { De_updatePatientSocialHistoryInput } from "@/modules/patients/services/de/__generated__/deUpdatePatientSocialHistoryMutation.graphql"
import { De_verifyHealthInformationInput } from "@/modules/patients/services/de/__generated__/deVerifyHealthInformationMutation.graphql"
import { SocialHistoryFragment } from "@/modules/patients/services/SocialHistoryFragment"
import { usePatientSocialHistory } from "@/modules/patients/services/usePatientSocialHistory"
import { useUpdatePatientSocialHistoryMutation } from "@/modules/patients/services/useUpdatePatientSocialHistoryMutation"
import { useVerifyHealthInformation } from "@/modules/patients/services/useVerifyHealthInformation"
import { useDrawerStore } from "@/stores/useDrawerStore"

const SMOKING_HABIT_OPTIONS: SmokingHabit[] = ["REGULARLY", "IN_COMPANY", "NO_LONGER", "NEVER", "NOT_SPECIFIED"]
const DRINKING_HABIT_OPTIONS: DrinkingHabit[] = [
  "DAILY",
  "FOUR_TO_FIVE_PER_WEEK",
  "ONE_TO_THREE_PER_WEEK",
  "LESS_THAN_ONE_TIMES_PER_WEEK",
  "NO_LONGER",
  "NEVER",
  "NOT_SPECIFIED",
]

const socialHistorySchema = z.object({
  comment: z.string().nullable(),
  smokingHabit: z.custom<SmokingHabit>(),
  drinkingHabit: z.custom<DrinkingHabit>(),
})

type SocialHistoryForm = z.infer<typeof socialHistorySchema>

const SocialHistoryDrawer = ({
  canVerify,
  verifiedBy,
  verifiedAt,
}: {
  canVerify: boolean
  verifiedBy: string
  verifiedAt: string
}) => {
  const isEditMode = useEditMode()
  const { t } = useTranslation()
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()
  const country = useCountryParam()
  const handleUpdateSocialHistory = useUpdatePatientSocialHistoryMutation(country)
  const handleVerifyHealthInformation = useVerifyHealthInformation(country)
  const dataRef = usePatientSocialHistory(patientId, country)
  const data = useFragment<SocialHistoryFragment$key>(SocialHistoryFragment, dataRef)
  const { comment, smokingHabit, drinkingHabit } = data

  const form = useForm<SocialHistoryForm>({
    resolver: zodResolver(socialHistorySchema),
    defaultValues: {
      comment,
      smokingHabit,
      drinkingHabit,
    },
  })
  const {
    handleSubmit,
    formState: { isDirty },
  } = form

  const handleVerifySocialHistory = () => {
    const verification: De_verifyHealthInformationInput & Ch_verifyHealthInformationInput = {
      patientId,
      healthInformationType: "SOCIAL_HISTORY",
      appointmentId: appointmentId!,
    }

    handleVerifyHealthInformation(verification, closeDrawer)
  }

  const handleSaveSocialHistory = (data: SocialHistoryForm) => {
    if (isDirty) {
      const submitData: De_updatePatientSocialHistoryInput & Ch_updatePatientSocialHistoryInput = {
        comment: data.comment,
        drinkingHabit: data.drinkingHabit,
        smokingHabit: data.smokingHabit,
        patientId,
      }
      handleUpdateSocialHistory(submitData, closeDrawer)
    } else {
      closeDrawer()
    }
  }

  const handleClose = useCallback(() => {
    FaroInsightsWrapper.pushEvent("healthInformation_social_close")
    closeDrawer()
  }, [closeDrawer])

  useDirtyDrawerMaker(isDirty, handleClose)

  return (
    <Drawer open onOpenChange={handleClose}>
      <DrawerContent data-testid="social-history-drawer-content">
        <DrawerClose className="top-2 right-2" />
        <DrawerHeader className="border-b py-2">
          <DrawerTitle
            data-testid="social-history-drawer-title"
            className="animate-fade-in-bottom text-on-surface-subdued text-sm leading-10 font-medium">
            {t("patient.healthInformation.socialHistory.title")}
          </DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="p-0">
          <ScrollWrapper className="p-4">
            <div className="flex flex-col gap-8">
              <FormProvider {...form}>
                {isEditMode && <SocialHistoryEditableBody />}
                {!isEditMode && <SocialHistoryReadonlyBody />}
              </FormProvider>
            </div>
          </ScrollWrapper>
        </DrawerBody>
        <DrawerFooter className="justify-end gap-2 overflow-hidden">
          {verifiedBy !== "" && <ConfirmedBy verifiedBy={verifiedBy} verifiedAt={verifiedAt} />}
          <Button data-testid="social-history-drawer-close-cancel-button" variant="secondary" onClick={handleClose}>
            {isEditMode ? t("common.button.cancel") : t("common.button.close")}
          </Button>
          {isEditMode && !canVerify && (
            <Button
              data-testid="social-history-drawer-save-button"
              onClick={() => {
                FaroInsightsWrapper.pushEvent("healthInformation_social_save")
                handleSubmit(handleSaveSocialHistory)()
              }}>
              {t("common.button.save")}
            </Button>
          )}
          {isEditMode && canVerify && (
            <Button
              data-testid="social-history-drawer-save-button"
              onClick={() => {
                handleSubmit(handleSaveSocialHistory)()
                handleVerifySocialHistory()
              }}>
              {t("common.button.verify")}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

const SocialHistoryEditableBody = () => {
  const { t } = useTranslation()

  return (
    <>
      <CommentTextArea data-testid="social-history-drawer-comment" fieldName="comment" maxLength={200} />
      <div className="flex flex-col gap-4">
        <span data-testid="social-history-drawer-habits-title" className="text-on-surface text-base font-medium">
          {t("patient.healthInformation.socialHistory.habits")}
        </span>
        <SelectFormField
          data-testid="social-history-drawer-smoking-habit"
          fieldName="smokingHabit"
          label={t("patient.healthInformation.socialHistory.smokingHabit.question")}
          options={SMOKING_HABIT_OPTIONS.map((option) => ({
            label: t(`patient.healthInformation.socialHistory.smokingHabit.options.${option}`),
            value: option,
          }))}
        />
        <SelectFormField
          data-testid="social-history-drawer-drinking-habit"
          fieldName="drinkingHabit"
          label={t("patient.healthInformation.socialHistory.drinkingHabit.question")}
          options={DRINKING_HABIT_OPTIONS.map((option) => ({
            label: t(`patient.healthInformation.socialHistory.drinkingHabit.options.${option}`),
            value: option,
          }))}
        />
      </div>
    </>
  )
}

const SocialHistoryReadonlyBody = () => {
  const { t } = useTranslation()
  const { getValues } = useFormContext()

  return (
    <>
      <ReadOnlyFormField dataTestId="social-history-drawer-comment" label={t("common.comment")}>
        <CommentTextArea isEditMode={false} fieldName="comment" maxLength={200} />
      </ReadOnlyFormField>
      <div className="flex flex-col gap-4">
        <span data-testid="social-history-drawer-habits-title" className="text-on-surface text-base font-medium">
          {t("patient.healthInformation.socialHistory.habits")}
        </span>
        <ReadOnlyFormField
          dataTestId="social-history-drawer-smoking-habit"
          label={t("patient.healthInformation.socialHistory.smokingHabit.question")}>
          {t(`patient.healthInformation.socialHistory.smokingHabit.options.${getValues("smokingHabit")}`)}
        </ReadOnlyFormField>
        <ReadOnlyFormField
          dataTestId="social-history-drawer-drinking-habit"
          label={t("patient.healthInformation.socialHistory.drinkingHabit.question")}>
          {t(`patient.healthInformation.socialHistory.drinkingHabit.options.${getValues("drinkingHabit")}`)}
        </ReadOnlyFormField>
      </div>
    </>
  )
}

export { SocialHistoryDrawer }
